import React, { useId, useRef, useState } from 'react'
import { mergeProps } from 'react-aria'
import { useListState, Selection } from 'react-stately'
import invariant from 'tiny-invariant'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Popover } from '../../../core-components/popover/Popover'
import { Box } from '../../../core-components'
import { useElementScrolledOut } from '../../../internal-helpers/useElementScrolledOut'
import { SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { PositioningProps } from '../../types'
import { useElementWidth } from '../../hooks'
import { Expandable, useExpandable } from '../../input/pill-combobox/Expandable'
import { MIN_SELECT_LIST_WIDTH } from '../base-select/constants'
import { useSelectListData } from '../select-list/useSelectListData'
import { CascadeSelectItem } from '../select/PublicComponents'
import {
  DynamicCascadeItem,
  dynamicItemToItem,
  DynamicSimpleItem,
} from '../base-select/dynamicItemUtils'
import {
  getStringValue,
  isCascadeSelectValue,
  isCascadeSelectValues,
} from '../base-select/cascadeSelectUtils'
import { CascadeListFocusProvider } from '../base-select/CascadeListFocusContext'
import { SelectItemPlaceholder } from '../select-item/SelectItemPlaceholder'
import { CascadeItems } from '../base-select/CascadeItemSelectList'
import { Label, Pills, SelectContainer } from './PillsSelectContent'
import { PillSelectList } from './PillSelectList'

type Variant = 'regular' | 'nested'

export type CascadeSelectValue = { value: string; path: string[] }

type SimpleItemsValuesProps = {
  onChange: (value: Array<string>) => void
  values: ReadonlyArray<string>
}

export type CascadeItemsValuesProps = {
  onChange: (value: Array<CascadeSelectValue>) => void
  values: ReadonlyArray<CascadeSelectValue>
}

type Props<Value extends string | CascadeSelectValue> = {
  INTERNAL_isTriggerOpen?: boolean
  children: React.ReactNode
  'data-test-id'?: string
  isDisabled?: boolean
  isExpandable?: boolean
  isAlwaysCollapsed?: boolean
  label: React.ReactNode
  placeholder?: string
  variant?: Variant
  onChange: (value: Array<Value>) => void
  values: ReadonlyArray<Value>
  isLoadingSelected?: boolean
  /** <PillSelect.Item> of current selection, required for paginated data and external search */
  selectedItems?: React.ReactNode[]
  size?: 'regular' | 'large'
} & PositioningProps &
  (
    | {
        onSearchChange?: (value: string) => void
        search?: string
        hasUserProvidedSearch?: boolean
        hasSearch?: never
      }
    | {
        onSearchChange?: never
        search?: never
        hasUserProvidedSearch?: never
        hasSearch?: boolean
      }
  )

// TODO: Refactor in the future to make it accessible
export const PillSelect = <Value extends string | CascadeSelectValue = string>(
  props: Props<Value>
) => {
  const {
    values,
    children,
    label,
    onChange,
    isDisabled,
    'data-test-id': dataTestId,
    placeholder = 'Select...',
    variant = 'regular',
    INTERNAL_isTriggerOpen = false,
    onSearchChange,
    search: propsSearch,
    hasUserProvidedSearch,
    hasSearch,
    isExpandable,
    isAlwaysCollapsed,
    selectedItems,
    isLoadingSelected,
    size = 'regular',
    ...positioningProps
  } = props
  validatePositioningProps(positioningProps, 'PillSelect')
  const labelId = useId()

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const handleOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen)
    if (!isOpen) {
      handleSearchChange?.('')
      setSelectedCascadeItem(undefined)
    }
  }

  const triggerRef = useRef<HTMLDivElement>(null)
  const { getWidth: getTriggerWidth } = useElementWidth(triggerRef)

  const [searchValue, setSearchValue] = useState('')
  const [selectedCascadeItem, setSelectedCascadeItem] = useState<DynamicCascadeItem[] | undefined>()

  const search = propsSearch ?? searchValue
  const handleSearchChange = onSearchChange ?? (hasSearch ? setSearchValue : undefined)

  const listData = useSelectListData({
    children,
    selectedItems: isLoadingSelected ? undefined : selectedItems,
    componentName: 'PillSelect',
    // if we have cascade selected the search is only done in that panel, and we don't want to filter inside the component if user provides their own search
    search: selectedCascadeItem || hasUserProvidedSearch ? undefined : search,
  })

  const hasCascadeItems = listData.options
    .flatMap<
      DynamicCascadeItem | DynamicSimpleItem
    >(option => (option.itemType === 'section' ? option.children : option))
    .some(option => option.itemType === 'cascade')

  const handleCascadeSelectionChange = (selection: Selection) => {
    invariant(isCascadeSelectValues(values), 'Values should be CascadeSelectValue')

    const newValues =
      selection === 'all'
        ? values
        : [...selection].map(value => ({ value: value as string, path: [] }))
    return onChange(newValues as Value[])
  }

  const handleSimpleSelectionChange = (selection: Selection) => {
    const newValues: string[] =
      selection === 'all'
        ? [...listData.existingValues.values()]
        : ([...selection] as Array<string>)
    return (onChange as SimpleItemsValuesProps['onChange'])(newValues)
  }

  const listState = useListState({
    disabledKeys: listData.disabledKeys,
    children: dynamicItemToItem,
    items: listData.options,
    selectedKeys: values.map(value => (typeof value === 'string' ? value : value.path[0])),
    selectionMode: 'multiple',
    onSelectionChange: selection => {
      if (hasCascadeItems) {
        return handleCascadeSelectionChange(selection)
      } else {
        return handleSimpleSelectionChange(selection)
      }
    },
  })

  const expandableWrapperRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useElementScrolledOut(wrapperRef, () => {
    handleOpenChange(false)
  })
  const {
    expandableProps,
    visibleValues,
    isExpanded,
    isWrapping,
    invisibleValuesCount,
    valuesToRender,
  } = useExpandable<Value>({
    values,
    isExpandable,
    wrapperRef: expandableWrapperRef,
    wrapperPadding: 0,
    isOpen: isPopoverOpen,
    isAlwaysCollapsed,
  })
  const isOpen = isPopoverOpen || INTERNAL_isTriggerOpen

  return (
    <SettingLabelContainer
      isRequired={false}
      label={label}
      labelProps={{ id: labelId }}
      labelElement='div'
      {...positioningProps}
    >
      <Popover
        placement='bottom start'
        offset={8}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        trigger={triggerProps => {
          const commonProps = {
            rootProps: expandableProps,
            ariaProps: mergeProps(triggerProps, { 'aria-labelledby': labelId }),
            variant,
            isOpen,
            isDisabled,
          }

          const commonPillsProps = {
            items: valuesToRender.map(value => ({
              ...listData.itemData[getStringValue(value)],
              value: values.find(valueItem =>
                isCascadeSelectValue(valueItem) && isCascadeSelectValue(value)
                  ? valueItem.value === value.value &&
                    valueItem.path.join('.') === value.path.join('.')
                  : valueItem === value
              )!,
              isVisible: isWrapping || visibleValues.has(value),
            })),
            values,
            visibleValues: [...visibleValues.values()].map(value => ({
              ...listData.itemData[getStringValue(value)],
              value: getStringValue(value),
            })),
            onChange,
            isDisabled,
            placeholder,
          }

          return (
            <Box ref={wrapperRef}>
              <Expandable when={isExpanded} variant={variant}>
                <SelectContainer {...commonProps} data-test-id={dataTestId} triggerRef={triggerRef}>
                  <Pills<Value>
                    {...commonPillsProps}
                    pillsWrapperRef={expandableWrapperRef}
                    invisibleValuesCount={invisibleValuesCount}
                    isWrapping={isWrapping}
                    listState={listState}
                    isLoading={isLoadingSelected}
                    size={size}
                  />
                </SelectContainer>
              </Expandable>
            </Box>
          )
        }}
      >
        {!INTERNAL_isTriggerOpen && (
          <Box
            minWidth={Math.max(getTriggerWidth() ?? 0, MIN_SELECT_LIST_WIDTH)}
            maxHeight='inherit'
            overflow='hidden'
            borderRadius={8}
          >
            <CascadeListFocusProvider>
              <PillSelectList
                onClose={() => handleOpenChange(false)}
                aria-labelledby={labelId}
                values={values}
                onChange={onChange}
                onSearchChange={handleSearchChange}
                search={search}
                hasUserProvidedSearch={hasUserProvidedSearch}
                selectedCascadeItem={selectedCascadeItem}
                setSelectedCascadeItem={setSelectedCascadeItem}
                triggerWidth={getTriggerWidth() ?? 0}
                listState={listState}
                items={listData.itemData}
              />
            </CascadeListFocusProvider>
          </Box>
        )}
      </Popover>
    </SettingLabelContainer>
  )
}

PillSelect.Label = Label
PillSelect.Item = PillSelectList.Item
PillSelect.StartContent = PillSelectList.StartContent
PillSelect.EndContent = PillSelectList.EndContent
PillSelect.Section = PillSelectList.Section
PillSelect.CascadeItem = CascadeSelectItem
PillSelect.CascadeItems = CascadeItems
PillSelect.ItemPlaceholder = SelectItemPlaceholder
